import axios from 'axios';
import api from '../../../services/api';

export type FileManagerPathInfo = {
  key: string;
  name: string;
};

export type UploadProgress = {
  uploadedBytes: number;
  finalChunkStarted: boolean;
};

export const CHUNK_SIZE = 200000;

export const uploadFileInChunks = async (
  file: File,
  destinationPathInfo: FileManagerPathInfo[],
  signal: AbortSignal,
  onProgress: (progress: UploadProgress) => void
): Promise<void> => {
  if (file.size === 0) {
    throw new Error('Cannot upload an empty file.');
  }

  const uploadId = crypto.randomUUID();
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  let uploadedBytes = 0;

  for (let index = 0; index < totalChunks; index += 1) {
    const start = index * CHUNK_SIZE;
    const chunk = file.slice(start, Math.min(start + CHUNK_SIZE, file.size));
    const isLastChunk = index === totalChunks - 1;
    const chunkMetadata = {
      UploadId: uploadId,
      FileName: file.name,
      Index: index,
      TotalCount: totalChunks,
      FileSize: file.size,
    };
    const formData = new FormData();
    formData.append('chunk', chunk, 'blob');
    formData.append(
      'arguments',
      JSON.stringify({
        destinationPathInfo,
        chunkMetadata: JSON.stringify(chunkMetadata),
      })
    );
    formData.append('command', 'UploadChunk');

    await api.post('/umbraco/api/FileManager/Upload', formData, {
      params: { returnId: isLastChunk },
      signal,
      onUploadProgress: ({ loaded }) => {
        const currentChunkBytes = Math.min(loaded, chunk.size);
        onProgress({
          uploadedBytes: Math.min(uploadedBytes + currentChunkBytes, file.size),
          finalChunkStarted: isLastChunk,
        });
      },
    });

    uploadedBytes += chunk.size;
    onProgress({
      uploadedBytes,
      finalChunkStarted: isLastChunk,
    });
  }
};

export const isUploadCancelled = (error: unknown, signal: AbortSignal): boolean =>
  axios.isCancel(error) || signal.aborted;
